import {
  XOP_QUOTE_STATUS_LABELS,
  centsToUnits,
  type XopQuote,
} from "@xop-any-time/shared";
import { Box, Button, MenuItem, TextField, Typography } from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { CheckCircle2, Clock, FileText, Plus } from "lucide-react";
import { useState, type JSX } from "react";
import { Link as RouterLink, useNavigate } from "react-router-dom";

import { useAuth } from "../../../auth/AuthProvider.js";
import { NoAccessPanel } from "../../../components/ui/no-access-panel.js";
import { isPermissionDenied } from "../../../lib/api.js";
import { count, date, dateOnly, money } from "../../../lib/format.js";
import { listXopQuotes, xopQuoteKeys, type XopQuoteFilter } from "../../../lib/xopQuotesApi.js";
import { CrmTable, PageHeader, StatsCard, type Column } from "../crm.js";
import { XopQuoteStatusChip, validityPhrase } from "./quote-ui.js";

/**
 * Quotes — from XOP Networks, to this reseller (spec §5).
 *
 * ─────────────────────────────────────────────────────────────────────
 * THIS IS NOT THE PAGE THAT USED TO BE HERE, and the difference matters.
 * ─────────────────────────────────────────────────────────────────────
 * `/quotes` used to render the command center's quote builder: the
 * reseller's OWN quotes to ITS customers, `Invoice` rows with
 * `document_type: "quote"`. The product owner's ruling (spec §1) is that
 * mixing those with XOP Networks' quotes to the reseller is confusing, so
 * this section now holds exactly one kind of document — the one where the
 * reseller is the customer — and the reseller's own customer documents live
 * with each customer, in the file manager.
 *
 * The rows come from crm-api's `reseller_quotes`, which are priced from the
 * price list by XOP Networks. Nothing on this page is editable: a reseller
 * REQUESTS a quote, then accepts or declines the one it is sent.
 *
 * THE PAGE ALWAYS FETCHES `all` — ONE QUERY — and the status filter is
 * applied CLIENT-SIDE, to the table's rows only. The three cards above the
 * table ("Waiting on you", "Accepted", "Next to lapse") are the reseller's
 * global standing with XOP Networks, not a summary of whatever the table
 * happens to be filtered to right now; deriving them from a filtered fetch
 * made "Waiting on you" go to zero the moment someone picked "Accepted" in
 * the dropdown, which is the wrong number every time it isn't already "all".
 * `expired` being a computed status (`open` plus a `valid_until` in the
 * past, decided by crm-api's clock, not the browser's) is exactly why this
 * is safe: the one "all" fetch already carries each quote's
 * `effectiveStatus` as crm-api computed it, so filtering that array in the
 * browser never has to guess at today's date. `listXopQuotes` still takes a
 * status filter for other callers; this page just always passes `"all"`.
 */

const FILTERS: ReadonlyArray<{ value: XopQuoteFilter; label: string }> = [
  { value: "all", label: "All quotes" },
  { value: "open", label: XOP_QUOTE_STATUS_LABELS.open },
  { value: "accepted", label: XOP_QUOTE_STATUS_LABELS.accepted },
  { value: "declined", label: XOP_QUOTE_STATUS_LABELS.declined },
  { value: "expired", label: XOP_QUOTE_STATUS_LABELS.expired },
];

export function QuotesPage(): JSX.Element {
  const navigate = useNavigate();
  const { can } = useAuth();
  const [filter, setFilter] = useState<XopQuoteFilter>("all");

  // Always "all" — see the block comment above. The dropdown's `filter`
  // state below only ever narrows what the TABLE renders.
  const { data, isPending, error } = useQuery({
    queryKey: xopQuoteKeys.list("all"),
    queryFn: () => listXopQuotes("all"),
  });

  const quotes = data?.quotes ?? [];
  // The three cards read from the UNFILTERED set — the reseller's global
  // standing — regardless of what the table below is filtered to.
  const openCount = quotes.filter((q) => q.effectiveStatus === "open").length;
  const acceptedCount = quotes.filter((q) => q.effectiveStatus === "accepted").length;
  // The soonest lapse among the quotes that can still be acted on.
  const nextToLapse = quotes
    .filter((q) => q.effectiveStatus === "open" && q.validUntil !== null)
    .sort((a, b) => String(a.validUntil).localeCompare(String(b.validUntil)))[0];
  // The table's own rows: the same set, narrowed to the selected filter.
  const visibleQuotes = filter === "all" ? quotes : quotes.filter((q) => q.effectiveStatus === filter);

  const columns: ReadonlyArray<Column<XopQuote>> = [
    {
      key: "number",
      header: "Quote",
      render: (q) => (
        <Box>
          <Typography sx={{ fontSize: 13.5, fontWeight: 600, fontFamily: "monospace" }}>
            {q.number ?? `#${q.id}`}
          </Typography>
          <Typography sx={{ fontSize: 11.5, color: "text.secondary" }}>
            {q.lines.length === 1 ? "1 line" : `${q.lines.length} lines`}
          </Typography>
        </Box>
      ),
    },
    {
      // WHICH of the reseller's customers the quote is for (2026-09-15) — the
      // name as it stood when XOP issued it, so a later rename does not rewrite
      // the document. A quote requested before the form asked for a customer
      // has none, and says so rather than leaving a blank cell.
      key: "customer",
      header: "Customer",
      render: (q) =>
        q.customerName ? (
          <Typography data-testid="xop-quote-customer" sx={{ fontSize: 13.5, fontWeight: 500 }}>
            {q.customerName}
          </Typography>
        ) : (
          <Typography data-testid="xop-quote-customer" sx={{ fontSize: 13.5, color: "text.secondary" }}>
            —
          </Typography>
        ),
    },
    { key: "issued", header: "Issued", render: (q) => date(q.issuedAt) },
    {
      key: "valid",
      header: "Validity",
      render: (q) => (
        <Typography sx={{ fontSize: 13.5, color: "text.secondary" }}>
          {validityPhrase(q)}
        </Typography>
      ),
    },
    {
      key: "total",
      header: "Total",
      align: "right",
      // The QUOTE's currency, not the reseller's: XOP Networks priced this,
      // and no conversion happens anywhere in this feature (spec §5).
      render: (q) => money(centsToUnits(q.totalCents), q.currency),
    },
    {
      key: "status",
      header: "Status",
      align: "right",
      render: (q) => <XopQuoteStatusChip status={q.effectiveStatus} />,
    },
  ];

  if (isPermissionDenied(error)) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <PageHeader title="Quotes" description={DESCRIPTION} />
        <NoAccessPanel permission={error.permission ?? "invoices:view"} />
      </Box>
    );
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      <PageHeader
        title="Quotes"
        description={DESCRIPTION}
        actions={
          // A COURTESY, not a gate: crm-api refuses the POST without
          // `invoices:edit` regardless. See `auth/usePermissions.ts`.
          can("invoices:edit") ? (
            <Button
              component={RouterLink}
              to="/quotes/request"
              variant="contained"
              startIcon={<Plus size={16} />}
            >
              Request a quote
            </Button>
          ) : null
        }
      />

      <Box
        sx={{
          display: "grid",
          gap: 2,
          gridTemplateColumns: { xs: "1fr", sm: "repeat(3, 1fr)" },
        }}
      >
        <Box data-testid="xop-quotes-open">
          <StatsCard title="Waiting on you" value={count(openCount)} icon={Clock} iconColor="#ea580c" loading={isPending} />
        </Box>
        <Box data-testid="xop-quotes-accepted">
          <StatsCard title="Accepted" value={count(acceptedCount)} icon={CheckCircle2} iconColor="#16a34a" loading={isPending} />
        </Box>
        <Box data-testid="xop-quotes-next">
          <StatsCard
            title="Next to lapse"
            value={nextToLapse ? dateOnly(nextToLapse.validUntil) : "—"}
            icon={FileText}
            loading={isPending}
          />
        </Box>
      </Box>

      <CrmTable
        columns={columns}
        rows={visibleQuotes}
        loading={isPending}
        error={error ? (error as Error).message : null}
        empty={
          filter === "all"
            ? "XOP Networks has not sent you a quote yet. Request one to get started."
            : `No ${XOP_QUOTE_STATUS_LABELS[filter].toLowerCase()} quotes.`
        }
        rowKey={(q) => q.id}
        onRowClick={(q) => navigate(`/quotes/${q.id}`)}
        toolbar={
          <TextField
            select
            size="small"
            label="Filter by status"
            value={filter}
            onChange={(e) => setFilter(e.target.value as XopQuoteFilter)}
            sx={{ minWidth: 200 }}
          >
            {FILTERS.map((f) => (
              <MenuItem key={f.value} value={f.value}>
                {f.label}
              </MenuItem>
            ))}
          </TextField>
        }
      />
    </Box>
  );
}

const DESCRIPTION =
  "Quotes XOP Networks has issued to you, priced from the XOP Networks price list. Accept one to place the order.";

export default QuotesPage;
